import { z } from 'zod';

import type { UserStatus } from './user.js';

/**
 * The office profile of ONE driver — the detail view behind each row of
 * Persons → Drivers. A "driver" is not a separate entity: it's a staff `user`
 * whose role grants `delivery.deliver`. So this is a read-only aggregation over
 * data that already exists (the `deliveries` dispatch board + its
 * proof-of-delivery `attachments`), scoped to the tenant AND that user.
 */

/** Identity block — the user themselves, plus the role names that qualify them. */
export interface DriverIdentity {
  id: string;
  email: string;
  fullName: string;
  phone: string | null;
  locale: string;
  status: UserStatus;
  lastLoginAt: string | null;
  createdAt: string;
  /** Every role the user holds (names). */
  roles: string[];
  /** The subset of `roles` that actually grant `delivery.deliver`. */
  driverRoles: string[];
}

/** Delivery counters. `successRate` is null until at least one job finished. */
export interface DriverStats {
  total: number;
  delivered: number;
  failed: number;
  pending: number;
  assigned: number;
  outForDelivery: number;
  /** assigned + out_for_delivery + pending — anything still open. */
  inProgress: number;
  /** delivered / (delivered + failed), 0-100, rounded to 1dp. Null when none finished. */
  successRate: number | null;
}

/** One row of the driver's delivery history. */
export interface DriverDeliveryRow {
  id: string;
  docNumber: string;
  status: string;
  salesOrderId: string;
  sourceNumber: string | null;
  partyId: string;
  partyName: string;
  recipientName: string | null;
  city: string | null;
  orderTotal: string | null;
  currencyCode: string | null;
  assignedAt: string | null;
  deliveredAt: string | null;
  createdAt: string;
  proofAttachmentId: string | null;
}

/** A proof-of-delivery photo this driver captured, with its delivery context. */
export interface DriverProofPhoto {
  attachmentId: string;
  filename: string;
  mimeType: string;
  sizeBytes: number;
  uploadedAt: string;
  deliveryId: string;
  deliveryNumber: string;
  deliveryStatus: string;
  recipientName: string | null;
}

export interface DriverProfile {
  user: DriverIdentity;
  /** False when the user holds no role granting `delivery.deliver` (yet). */
  isDriver: boolean;
  /**
   * False when this tenant has no `deliveries` table / the delivery module was
   * never migrated in. The page then shows an explanatory empty state instead
   * of zeroes that look like a real (bad) score.
   */
  deliveryDataAvailable: boolean;
  stats: DriverStats;
  /** Newest-first delivery history (capped). */
  recent: DriverDeliveryRow[];
  proofs: DriverProofPhoto[];
  /** Per-currency display decimals (IQD → 0) for money on the page. */
  currencyDecimals: Record<string, number>;
}

/**
 * Find-or-create a role and grant it permissions. Exists so a tenant with NO
 * role granting `delivery.deliver` isn't a dead end in the Add-driver flow —
 * the UI can mint a "Driver" role on the spot. Idempotent on `code`.
 */
export const RoleCreateSchema = z.object({
  code: z.string().trim().min(1).max(64).regex(/^[a-z0-9_-]+$/, 'lowercase letters, digits, _ and - only'),
  name: z.string().trim().min(1).max(100),
  permissionCodes: z.array(z.string().min(1).max(100)).min(1).max(200),
  /**
   * Which apps this role opens. Omitted or empty means every app — the same
   * "empty means unrestricted" convention as pos_terminal_users and
   * user_warehouses, so a role says nothing about apps unless somebody decides
   * it should.
   */
  surfaces: z.array(z.enum(['web', 'till', 'rep'])).optional(),
});

export type RoleCreate = z.infer<typeof RoleCreateSchema>;

/**
 * Editing an existing role.
 *
 * `code` is absent on purpose and cannot be changed on any role, system or not:
 * four places in the app look roles up by literal code and silently find
 * nothing if one is renamed.
 *
 * `permissionCodes` allows an EMPTY array here where create requires at least
 * one — clearing a role down to nothing is a legitimate way to park it, and
 * `.min(1)` would make that impossible to express.
 */
export const RoleUpdateSchema = z.object({
  name: z.string().trim().min(1).max(100).optional(),
  permissionCodes: z.array(z.string().min(1).max(100)).max(200).optional(),
});

export type RoleUpdate = z.infer<typeof RoleUpdateSchema>;
